// Remote DC dashboard, configuration, presets and runs.
import {
  RemoteDataCollectionCategoryOptionsResponse,
  RemoteDataCollectionChainPresets,
  RemoteDataCollectionChainSnapshot,
  RemoteDataCollectionConfig,
  RemoteDataCollectionConfigUpdateRequest,
  RemoteDataCollectionDashboardResponse,
  RemoteDataCollectionDefaultPresetUpdateRequest,
  RemoteDataCollectionDefaults,
  RemoteDataCollectionPresetCreateRequest,
  RemoteDataCollectionPresetDeleteRequest,
  RemoteDataCollectionPresetsResponse,
  RemoteDataCollectionPresetUpdateRequest,
  RemoteDataCollectionRefreshRequest,
  RemoteDataCollectionRunResponse,
  RemoteDataCollectionTaskListsResponse,
} from '../../types/remoteDc';
import { forgeFetch, forgeRequest } from '../forgeRequest';
import { FORGE_DASHBOARDS_API_URL, FORGE_REMOTE_DATA_COLLECTION_API_URL } from './endpoints';

export async function fetchRemoteDataCollection(
  token: string
): Promise<RemoteDataCollectionDashboardResponse> {
  return forgeRequest<RemoteDataCollectionDashboardResponse>(
    token,
    `${FORGE_DASHBOARDS_API_URL}/remote-data-collection`,
    'Error fetching remote data collection dashboard',
    { dedupeKey: ['fetchRemoteDataCollection', token] }
  );
}

export async function refreshRemoteDataCollection(
  token: string,
  request: RemoteDataCollectionRefreshRequest
): Promise<RemoteDataCollectionChainSnapshot> {
  return forgeRequest<RemoteDataCollectionChainSnapshot>(
    token,
    `${FORGE_DASHBOARDS_API_URL}/remote-data-collection/refresh`,
    'Error refreshing remote data collection snapshot',
    { method: 'POST', body: request }
  );
}

export async function fetchRemoteDataCollectionConfig(
  token: string,
  params?: { nonAdminPreview?: boolean }
): Promise<RemoteDataCollectionDefaults> {
  const requestKey = `${token}:${params?.nonAdminPreview ? 'non-admin-preview' : 'default'}`;
  const query = new URLSearchParams();
  if (params?.nonAdminPreview) query.set('non_admin_preview', 'true');
  return forgeRequest<RemoteDataCollectionDefaults>(
    token,
    `${FORGE_REMOTE_DATA_COLLECTION_API_URL}/config${query.toString() ? `?${query.toString()}` : ''}`,
    'Error fetching Remote DC configuration',
    { dedupeKey: ['fetchRemoteDataCollectionConfig', requestKey] }
  );
}

export async function fetchRemoteDataCollectionConfigDefaults(
  token: string
): Promise<RemoteDataCollectionConfig> {
  return forgeRequest<RemoteDataCollectionConfig>(
    token,
    `${FORGE_REMOTE_DATA_COLLECTION_API_URL}/config/defaults`,
    'Error fetching Remote DC configuration'
  );
}

export async function updateRemoteDataCollectionConfigDefaults(
  token: string,
  request: RemoteDataCollectionConfigUpdateRequest
): Promise<RemoteDataCollectionConfig> {
  return forgeRequest<RemoteDataCollectionConfig>(
    token,
    `${FORGE_REMOTE_DATA_COLLECTION_API_URL}/config/defaults`,
    'Error updating Remote DC configuration',
    { method: 'PUT', body: request }
  );
}

export async function fetchRemoteDataCollectionModCategories(
  token: string,
  chainId: number
): Promise<RemoteDataCollectionCategoryOptionsResponse> {
  const query = new URLSearchParams({ chain_id: String(chainId) });
  return forgeRequest<RemoteDataCollectionCategoryOptionsResponse>(
    token,
    `${FORGE_REMOTE_DATA_COLLECTION_API_URL}/mod-categories?${query.toString()}`,
    'Error fetching Remote DC Mod categories'
  );
}

export async function fetchRemoteDataCollectionPresets(
  token: string
): Promise<RemoteDataCollectionPresetsResponse> {
  return forgeRequest<RemoteDataCollectionPresetsResponse>(
    token,
    `${FORGE_REMOTE_DATA_COLLECTION_API_URL}/presets`,
    'Error fetching Remote DC presets'
  );
}

export async function createRemoteDataCollectionPreset(
  token: string,
  request: RemoteDataCollectionPresetCreateRequest
): Promise<RemoteDataCollectionChainPresets> {
  return forgeRequest<RemoteDataCollectionChainPresets>(
    token,
    `${FORGE_REMOTE_DATA_COLLECTION_API_URL}/presets`,
    'Error creating Remote DC preset',
    { method: 'POST', body: request }
  );
}

export async function updateRemoteDataCollectionPreset(
  token: string,
  presetId: string,
  request: RemoteDataCollectionPresetUpdateRequest
): Promise<RemoteDataCollectionChainPresets> {
  return forgeRequest<RemoteDataCollectionChainPresets>(
    token,
    `${FORGE_REMOTE_DATA_COLLECTION_API_URL}/presets/${encodeURIComponent(presetId)}`,
    'Error updating Remote DC preset',
    { method: 'PUT', body: request }
  );
}

export async function deleteRemoteDataCollectionPreset(
  token: string,
  presetId: string,
  request: RemoteDataCollectionPresetDeleteRequest
): Promise<RemoteDataCollectionChainPresets> {
  return forgeRequest<RemoteDataCollectionChainPresets>(
    token,
    `${FORGE_REMOTE_DATA_COLLECTION_API_URL}/presets/${encodeURIComponent(presetId)}`,
    'Error deleting Remote DC preset',
    { method: 'DELETE', body: request }
  );
}

export async function updateRemoteDataCollectionDefaultPreset(
  token: string,
  request: RemoteDataCollectionDefaultPresetUpdateRequest
): Promise<RemoteDataCollectionChainPresets> {
  return forgeRequest<RemoteDataCollectionChainPresets>(
    token,
    `${FORGE_REMOTE_DATA_COLLECTION_API_URL}/default-preset`,
    'Error selecting the default Remote DC preset',
    { method: 'PUT', body: request }
  );
}

export async function exportRemoteDataCollectionWorkItems(
  token: string,
  params: { chainId: number; modTagPrefix?: string | null; taskListId?: string | null }
): Promise<{ blob: Blob; filename: string }> {
  const query = new URLSearchParams();
  query.set('chain_id', String(params.chainId));
  if (params?.modTagPrefix) query.set('mod_tag_prefix', params.modTagPrefix);
  if (params?.taskListId) query.set('task_list_id', params.taskListId);
  const response = await forgeFetch(
    token,
    `${FORGE_REMOTE_DATA_COLLECTION_API_URL}/work-items/export${query.toString() ? `?${query.toString()}` : ''}`,
    'Error exporting Remote DC work items'
  );
  // The export filename is deliberately fixed, ignoring content-disposition.
  return {
    blob: await response.blob(),
    filename: `Remote DC ${params.chainId}.csv`,
  };
}

export async function createRemoteDataCollectionRun(
  token: string,
  request: RemoteDataCollectionRefreshRequest
): Promise<RemoteDataCollectionRunResponse> {
  return forgeRequest<RemoteDataCollectionRunResponse>(
    token,
    `${FORGE_REMOTE_DATA_COLLECTION_API_URL}/runs`,
    'Error creating remote data collection run',
    { method: 'POST', body: request }
  );
}

export async function fetchRemoteDataCollectionTaskLists(
  token: string,
  chainId: number,
  params?: { signal?: AbortSignal }
): Promise<RemoteDataCollectionTaskListsResponse> {
  return forgeRequest<RemoteDataCollectionTaskListsResponse>(
    token,
    `${FORGE_REMOTE_DATA_COLLECTION_API_URL}/task-lists?chain_id=${encodeURIComponent(String(chainId))}`,
    'Error fetching remote data collection task lists',
    { signal: params?.signal }
  );
}

export async function fetchRemoteDataCollectionRun(
  token: string,
  runId: string,
  params?: { signal?: AbortSignal }
): Promise<RemoteDataCollectionRunResponse> {
  return forgeRequest<RemoteDataCollectionRunResponse>(
    token,
    `${FORGE_REMOTE_DATA_COLLECTION_API_URL}/runs/${encodeURIComponent(runId)}`,
    'Error fetching remote data collection run',
    { signal: params?.signal }
  );
}
